import { ChevronRight, Flag, RotateCcw, Target } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../components/Avatar.tsx';
import { useAuth } from '../lib/auth.tsx';
import type { RoundStats } from '../lib/rounds.ts';
import { getCurrentRound, getRoundStats } from '../lib/rounds.ts';
import { clubsToWork } from '../lib/clubFocus.ts';
import { INDEX_DEFAUT, formatHandicap, getHandicap } from '../lib/me.ts';
import { modeLabel } from '../lib/modes.ts';
import type { Stats, StatsWindow, TrainingMode } from '../lib/training.ts';
import { getCurrent, getStats, listModes } from '../lib/training.ts';

/* L'accueil répond à une seule question : « où j'en suis, et qu'est-ce que je
 * travaille aujourd'hui ». Tout ce qui s'affiche ici doit servir cette
 * décision — un chiffre qui ne change rien à la séance qui va suivre n'a rien
 * à y faire. Le détail complet — courbe, fautes, sac club par club — vit sur
 * l'écran Statistiques ; ici, un résumé qu'on lit sans faire défiler. */

const rate = (w: StatsWindow): number | null =>
    w.shots > 0 ? Math.round((w.center * 1000) / w.shots) / 10 : null;

const share = (n: number, total: number): number =>
    total > 0 ? Math.round((n * 1000) / total) / 10 : 0;

/** L'écart au par, tel qu'on l'annonce au club-house : rien à l'égalité,
 *  un signe sinon. */
function fmtEcart(n: number | null): string {
    if (n == null) return '—';
    if (n === 0) return 'par';
    return n > 0 ? `+${n}` : `−${Math.abs(n)}`;
}

export function Home() {
    /* Le tour en cours, s'il y en a un.
     *
     * Une requête de plus au chargement de l'accueil, et elle se paie : un
     * joueur qui rouvre l'application au trou 7 tombe sur le bouton qui le
     * ramène à sa carte, pas sur une liste de golfs où retrouver le sien. */
    const [enCours, setEnCours] = useState<{ courseName: string; trou: number } | null>(null);
    const [dernier, setDernier] = useState<{ courseId: string; courseName: string } | null>(null);

    /* La séance ouverte, symétrique du tour ouvert.
     *
     * L'accueil savait dire « reprendre au trou 7 » et ne disait rien d'un
     * exercice laissé en plan — alors que c'est le même oubli, avec la même
     * conséquence : tant qu'elle n'est pas terminée, une séance ne compte dans
     * aucune statistique. L'écran d'entraînement le signalait, mais il faut y
     * être allé ; l'accueil est celui qu'on ouvre.
     *
     * Le nombre de balles est comptable ici : `series` arrive avec la réponse,
     * et « 12 balles » dit mieux que l'heure ce qu'on risque de perdre. */
    const [seance, setSeance] = useState<{ nom: string; balles: number } | null>(null);

    useEffect(() => {
        void getCurrent()
            .then((r) => {
                if (!r.session) return;
                setSeance({
                    nom: r.session.exerciseName ?? modeLabel(r.session.mode),
                    balles: (r.series ?? []).reduce((n, se) => n + se.shots.length, 0),
                });
            })
            .catch(() => { /* l'accueil se passe très bien de cette information */ });
    }, []);

    useEffect(() => {
        void getCurrentRound()
            .then((r) => {
                setDernier(r.lastPlayed ?? null);
                if (!r.round) return;
                // Le trou où l'on en est : le premier non joué.
                const joues = r.myHoles.filter((h) => h.strokes != null).length;
                setEnCours({
                    courseName: r.round.courseName,
                    trou: Math.min(joues + 1, r.round.holeCount),
                });
            })
            .catch(() => { /* l'accueil se passe très bien de cette information */ });
    }, []);

    /* Le score, symétrique de la technique : l'accueil ne racontait que le
     * practice, alors que Parcours est l'autre moitié de l'application.
     *
     * Le DERNIER tour, et non la moyenne des dix. Un accueil qu'on ouvre le
     * soir en rentrant du golf répond à « qu'est-ce que j'ai fait
     * aujourd'hui » ; une moyenne sur dix tours bouge à peine d'une sortie à
     * l'autre et ne répond à personne. La tendance garde sa place sur
     * l'écran Statistiques, où la courbe l'accompagne.
     *
     * « dernier » est résolu par le serveur : l'accueil ne connaît pas
     * l'identifiant du tour, et aller le chercher d'abord ferait deux
     * allers-retours enchaînés pour une seule tuile. */
    const [roundStats, setRoundStats] = useState<RoundStats | null>(null);
    useEffect(() => {
        void getRoundStats('dernier').then(setRoundStats)
            .catch(() => { /* pas de tour, pas de bloc */ });
    }, []);

    /* La tendance de l'index : l'écart entre l'index d'aujourd'hui et celui
     * qui était en vigueur il y a trente jours — c'est-à-dire la dernière
     * saisie antérieure à cette date, pas la plus vieille de l'historique.
     * Muette s'il n'y a rien d'assez ancien pour comparer : « en baisse de
     * 0 » n'apprend rien, et une tendance calculée sur deux saisies du même
     * jour serait un mensonge de présentation. */
    const [tendance, setTendance] = useState<number | null>(null);
    useEffect(() => {
        void getHandicap().then((h) => {
            if (!h.current) return;
            const seuil = Date.now() - 30 * 86_400_000;
            const reference = h.history.find((e) => new Date(e.recordedAt).getTime() <= seuil);
            if (!reference) return;
            const delta = h.current.indexValue - reference.indexValue;
            if (Math.abs(delta) >= 0.05) setTendance(Math.round(delta * 10) / 10);
        }).catch(() => { /* l'en-tête vit très bien sans la tendance */ });
    }, []);

    const { user } = useAuth();
    const [stats, setStats] = useState<Stats | null>(null);
    const [modes, setModes] = useState<TrainingMode[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // L'accueil ne bloque jamais sur ses statistiques : le bouton de
        // démarrage est déjà affiché et cliquable pendant le chargement.
        void Promise.all([getStats(), listModes()])
            .then(([s, m]) => { setStats(s); setModes(m); })
            .catch(() => { /* l'écran reste utilisable sans ses chiffres */ })
            .finally(() => setLoading(false));
    }, []);

    // Le mode délaissé depuis le plus longtemps, jamais essayé en premier.
    //
    // Une suggestion, pas une consigne : elle ouvre la liste des exercices,
    // elle n'en lance aucun. Elle ne s'affiche que s'il y a vraiment un choix
    // à faire — proposer le seul mode existant serait du bruit — et que le mode
    // est soit inédit, soit oublié depuis trois semaines. En deçà, alterner
    // n'apporte rien et le rappel deviendrait un reproche permanent.
    //
    // Muette tant qu'aucune séance n'a été terminée : à ce stade il n'y a rien
    // à alterner, et désigner un exercice au hasard reviendrait à conseiller un
    // joueur dont on ne sait encore rien.
    const STALE_DAYS = 21;
    const candidate = modes.length > 1 && (stats?.overview.sessionsTotal ?? 0) > 0
        ? [...modes].sort((a, b) => {
            if (!a.lastSessionAt) return b.lastSessionAt ? -1 : 0;
            if (!b.lastSessionAt) return 1;
            return a.lastSessionAt.localeCompare(b.lastSessionAt);
        })[0]
        : undefined;
    const neglected = candidate && (
        candidate.sessionCount === 0
        || Date.now() - new Date(candidate.lastSessionAt!).getTime() > STALE_DAYS * 86_400_000
    ) ? candidate : undefined;

    const recentRate = stats ? rate(stats.recent) : null;

    /* La suggestion pour la prochaine fois.
     *
     *  Un seul conseil, jamais trois : une page qui propose trois priorités n'en
     *  propose aucune. Le club le plus en difficulté passe devant — au sens du
     *  classement tempéré, donc jamais un club à peine joué ; à défaut, un club
     *  oublié, parce qu'un club qu'on ne sort pas ne progressera jamais. */
    const suggestion: { titre: string; raison: string } | null = (() => {
        const pire = clubsToWork(stats)[0];
        if (pire) {
            return {
                titre: pire.label,
                raison: `C'est là que vous êtes le plus en difficulté : `
                    + `${share(pire.center, pire.shots)} % de réussite sur ${pire.shots} balles.`,
            };
        }
        const oubli = stats?.neglected[0];
        if (oubli) {
            return {
                titre: oubli.label,
                raison: oubli.lastPlayedAt
                    ? "Vous ne l'avez pas sorti depuis longtemps — de quoi perdre vos repères."
                    : "Vous ne l'avez encore jamais joué : autant savoir ce qu'il donne.",
            };
        }
        return null;
    })();

    const hasData = (stats?.overview.sessionsTotal ?? 0) > 0;

    return (
        /* Le fond fairway est peint par AppShell sur toute la hauteur de
         * l'écran — voir là-bas. Ici, la page ne s'occupe que de son
         * contenu ; `text-base-content` referme le blanc hérité de la
         * texture, sans quoi chaque carte claire porterait du texte blanc.
         * Seul l'en-tête écrit en blanc, en explicite. */
        <div className="space-y-5 pb-8 pt-3 text-base-content md:pt-0">
            {/* L'en-tête du club-house, comme sur le mockup : la salutation
                en Fraunces, et l'INDEX en sous-titre — c'est la première
                chose qu'un golfeur dit de lui-même, elle se lit mieux en
                toutes lettres que tassée dans une pastille d'avatar. La
                tendance ne s'affiche que si l'historique la porte vraiment.
                Pas de marque golf.fevio.fr au-dessus : on est déjà dedans,
                se présenter à soi-même serait du bruit. */}
            <header className="pb-2 pt-2">
                <div className="flex items-center gap-3">
                    {/* L'anneau laiton reprend le rôle de l'ancienne marque
                        golf.fevio.fr : un seul trait doré qui distingue le
                        joueur, à la place d'une ligne de texte de plus. */}
                    <span className="inline-flex rounded-full p-0.5 ring-2 ring-accent">
                        <Avatar name={user?.displayName ?? ''} email={user?.email}
                            avatarUrl={user?.avatarUrl} size={48} />
                    </span>
                    <div className="min-w-0">
                        <h1 className="font-display truncate text-2xl font-bold tracking-tight text-white">
                            Bonjour {user?.displayName?.split(' ')[0]}
                        </h1>
                        {/* L'INDEX porte le laiton — la couleur de ce qu'on a
                            mérité, et rien d'autre sur cet en-tête. À 5,4:1
                            sur le fairway sombre, il tient largement. */}
                        <p className="mt-0.5 text-sm font-semibold text-accent">
                            {formatHandicap(user?.handicapIndex ?? INDEX_DEFAUT)} d'index
                            {tendance != null && (tendance < 0
                                ? ` · en baisse de ${Math.abs(tendance).toFixed(1).replace('.', ',')} ce mois-ci`
                                : ` · en hausse de ${tendance.toFixed(1).replace('.', ',')} ce mois-ci`)}
                        </p>
                    </div>
                </div>
            </header>

            {/* Les deux façons de jouer au golf, côte à côte.

                Aucune n'est un sous-cas de l'autre : on va au practice pour
                travailler, sur un parcours pour jouer. Mettre l'une en avant
                obligerait l'autre à passer par un menu, et ce sont les deux
                seules raisons d'ouvrir l'application.

                Alignées à gauche comme les tuiles du mockup — l'icône coiffe
                le titre, rien n'est centré : une tuile se lit comme une
                carte, pas comme un bouton. */}
            <div className="grid grid-cols-2 gap-3">
                <Link
                    to={seance ? '/entrainement/exercices' : '/entrainement'}
                    className="flex flex-col items-start gap-2 rounded-box bg-primary p-4 text-primary-content transition-transform active:scale-[0.98]"
                >
                    <Target size={26} />
                    <span className="text-base font-extrabold leading-tight">
                        {seance ? 'Reprendre' : 'Entraînement'}
                    </span>
                    <span className="-mt-1 text-xs leading-tight opacity-85">
                        {seance
                            ? `${seance.nom} · ${seance.balles} balle${seance.balles > 1 ? 's' : ''}`
                            : 'Choisir un exercice'}
                    </span>
                </Link>

                {/* Un tour ouvert change la destination ET le mot : on ne
                    « démarre » pas ce qu'on a laissé au trou 4.

                    Sinon, la tuile porte un raccourci vers le dernier golf
                    joué : on retourne au même bien plus souvent qu'on n'en
                    découvre un, et ce raccourci évite la liste, la recherche
                    et l'attente du GPS. Deux liens dans un bloc plutôt qu'un
                    bouton dans un lien — imbriquer les deux donnerait un
                    balisage invalide et une cible de doigt ambiguë. */}
                <div className="flex flex-col overflow-hidden rounded-box">
                    <Link
                        to={enCours ? '/parcours/jouer' : '/parcours'}
                        className="flex flex-1 flex-col items-start gap-2 bg-secondary p-4 text-secondary-content transition-transform active:scale-[0.98]"
                    >
                        <Flag size={26} />
                        <span className="text-base font-extrabold leading-tight">
                            {enCours ? 'Reprendre' : 'Parcours'}
                        </span>
                        <span className="-mt-1 text-xs leading-tight opacity-85">
                            {enCours
                                ? `${enCours.courseName} · trou ${enCours.trou}`
                                : 'Choisir un golf'}
                        </span>
                    </Link>
                    {dernier && !enCours && (
                        <Link
                            to={`/parcours/${dernier.courseId}/jouer`}
                            className="flex h-8 items-center justify-center gap-1 border-t border-secondary-content/25 bg-secondary px-2 text-secondary-content"
                        >
                            <RotateCcw size={13} aria-hidden="true" />
                            <span className="min-w-0 truncate text-xs">{dernier.courseName}</span>
                        </Link>
                    )}
                </div>
            </div>

            {/* Le score, à côté de la technique.
             *
             * L'accueil ne racontait que le practice — jusqu'ici Parcours
             * n'y apparaissait qu'en raccourci de départ, jamais en résultat.
             * Les repères choisis sont ceux qu'un golfeur connaît déjà :
             * l'écart au par, les putts, la répartition birdie / par / bogey
             * — pas des mesures inventées pour l'occasion. */}
            {roundStats && roundStats.rounds > 0 && (() => {
                /* Le net, quand les coups reçus sont connus.
                 *
                 * Une soustraction, et rien de plus : le net d'un tour, c'est
                 * son brut moins les coups reçus, trou par trou comme au total.
                 * Le détail par trou vit sur la carte de score ; ici, seul le
                 * total est demandé.
                 *
                 * `recus` à `null` — pas d'index, ou pas de tour précis — et
                 * tout retombe sur le brut : mieux vaut un seul chiffre juste
                 * qu'un net inventé à côté de lui. */
                const recus = roundStats.coupsRecus?.total ?? null;
                const brut = roundStats.trend[0]?.totalStrokes ?? null;
                const net = recus != null && brut != null ? brut - recus : null;
                const ecart = recus != null && roundStats.avgEcart != null
                    ? roundStats.avgEcart - recus
                    : roundStats.avgEcart;

                /* Ce qui a fait le score, dans l'ordre où un trou se joue :
                 * le départ, le green, puis ce qui a coûté en chemin. */
                const jeu = roundStats.jeu;
                const mesures: { valeur: ReactNode; mot: string }[] = [];
                if (jeu && jeu.teeHoles > 0) {
                    mesures.push({
                        mot: 'fairways touchés',
                        valeur: <>{jeu.fairways}<span className="opacity-40">/{jeu.teeHoles}</span></>,
                    });
                }
                if (jeu && jeu.greenHoles > 0) {
                    mesures.push({
                        mot: 'greens en régulation',
                        valeur: <>{jeu.greens}<span className="opacity-40">/{jeu.greenHoles}</span></>,
                    });
                }
                if (roundStats.sable.entres > 0) {
                    /* Les bunkers à QUITTER, et non les trous où il y en avait :
                     * deux bunkers sur le même trou sont deux bunkers à quitter.
                     * Le mot est celui de l'écran Statistiques, où le même
                     * nombre est détaillé — on ne rebaptise pas une mesure d'un
                     * écran à l'autre.
                     *
                     * Les échecs de sortie ne sont PAS écrits ici. Ils comptent,
                     * et c'est justement pourquoi ils ont droit à une carte
                     * entière sous « Détail » plutôt qu'à un bout de phrase
                     * accroché sous un chiffre, là où les trois libellés
                     * voisins tiennent en deux mots. */
                    mesures.push({
                        mot: `bunker${roundStats.sable.entres > 1 ? 's' : ''} à quitter`,
                        valeur: roundStats.sable.entres,
                    });
                }
                if (roundStats.penalites > 0) {
                    mesures.push({
                        mot: `coup${roundStats.penalites > 1 ? 's' : ''} de pénalité`,
                        valeur: roundStats.penalites,
                    });
                }
                return (
                <section className="rounded-box bg-base-100 shadow-md p-4">
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                            <Flag size={16} className="text-primary" /> Sur le parcours
                        </h2>
                        {/* Le détail s'ouvre SUR CE TOUR : on y va pour en savoir
                            plus sur les chiffres qu'on vient de lire, et
                            retomber sur la moyenne des dix serait changer de
                            sujet en chemin. */}
                        <Link to={roundStats.tour
                            ? `/statistiques?tour=${encodeURIComponent(roundStats.tour.id)}`
                            : '/statistiques'}
                            state={{ tab: 'rounds' }}
                            className="flex items-center gap-0.5 text-xs font-semibold text-primary">
                            Détail <ChevronRight size={14} />
                        </Link>
                    </div>

                    {/* Quel tour, et quand. Des chiffres de parcours sans le
                        parcours ni la date ne se rattachent à rien : c'est la
                        première chose qu'on vérifie avant de les lire. */}
                    {roundStats.tour && (
                        <p className="mt-1 truncate text-xs opacity-60">
                            {roundStats.tour.courseName}
                            {' · '}
                            {new Date(roundStats.tour.startedAt).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long',
                            })}
                        </p>
                    )}

                    {/* D'où vient le net, sur sa propre ligne.
                     *
                     * Le nombre de coups reçus est celui d'où sort tout le net,
                     * et un net affiché sans lui est invérifiable — c'est
                     * exactement ce qui a fait prendre un − 10 pour un
                     * résultat. « Plafonné » quand la limite a mordu : sans quoi
                     * un index 54 qui reçoit 36 coups reste inexplicable.
                     * « Calcul approché » quand le carnet ne porte ni slope ni
                     * SSS, plutôt que de faire passer ce net pour officiel. */}
                    {recus != null && (
                        <p className="truncate text-xs opacity-60">
                            {recus} coups reçus
                            {roundStats.coupsRecus?.plafonne ? ' · plafonné' : ''}
                            {roundStats.coupsRecus?.approche ? ' · calcul approché' : ''}
                        </p>
                    )}

                    {/* Le NET en grand, le brut dessous.
                     *
                     * Le net est le score de la partie — celui qui se compare à
                     * ce que dit la fédération — et le brut reste ce qu'on a
                     * réellement joué, qu'on retient aussi. Même disposition que
                     * sur la carte de score : deux écrans qui montrent le même
                     * tour ne doivent pas l'écrire de deux façons.
                     *
                     * Sans index connu, il n'y a pas de net : le brut reprend
                     * sa place en grand, seul, plutôt qu'un tiret sous lui. */}
                    {/* Les libellés sont calés en BAS de leur colonne : deux
                        d'entre elles portent un brut sous le chiffre, la
                        troisième non, et alignés en haut les trois mots
                        partaient en escalier. */}
                    <div className="mt-3 grid grid-cols-3 items-stretch gap-2 text-center">
                        <div className="flex flex-col">
                            <div className={`font-score text-2xl ${
                                ecart == null ? '' : ecart <= 0 ? 'text-success' : 'text-error'}`}>
                                {fmtEcart(ecart)}
                            </div>
                            {recus != null && (
                                <div className="font-score text-xs opacity-60">
                                    {fmtEcart(roundStats.avgEcart)} brut
                                </div>
                            )}
                            <div className="mt-auto pt-0.5 text-[10px] uppercase tracking-wide opacity-60">
                                au par
                            </div>
                        </div>
                        <div className="flex flex-col">
                            {/* Bornée à un tour, la « moyenne par tour » EST le
                                nombre de putts de ce tour : un entier, qu'on
                                écrit comme tel plutôt qu'en « 31,0 ». */}
                            <div className="font-score text-2xl">
                                {roundStats.avgPuttsRound != null
                                    ? Math.round(roundStats.avgPuttsRound)
                                    : '—'}
                            </div>
                            <div className="mt-auto pt-0.5 text-[10px] uppercase tracking-wide opacity-60">
                                putts
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="font-score text-2xl">
                                {(net ?? brut) ?? '—'}
                            </div>
                            {recus != null && brut != null && (
                                <div className="font-score text-xs opacity-60">{brut} brut</div>
                            )}
                            <div className="mt-auto pt-0.5 text-[10px] uppercase tracking-wide opacity-60">
                                coups
                            </div>
                        </div>
                    </div>

                    {/* Même vocabulaire visuel que la carte de score jouée :
                        une hauteur de barre par catégorie, pas un simple
                        décompte, pour qu'un coup d'œil suffise. */}
                    <div className="mt-4 flex items-stretch gap-1.5" style={{ height: '3.2rem' }}>
                        {(() => {
                            const d = roundStats.distribution;
                            const max = Math.max(1, d.eagle, d.birdie, d.par, d.bogey, d.double);
                            return ([
                                ['eagle', d.eagle, 'bg-success'],
                                ['birdie', d.birdie, 'bg-success/60'],
                                ['par', d.par, 'bg-base-300'],
                                ['bogey', d.bogey, 'bg-error/40'],
                                ['double+', d.double, 'bg-error'],
                            ] as const).map(([label, n, color]) => (
                                <div key={label} className="flex flex-1 flex-col items-center justify-end gap-1">
                                    <span className="font-score text-xs">{n}</span>
                                    <div className={`w-full rounded-t ${color}`}
                                        style={{ height: `${Math.max(6, (n / max) * 100)}%` }} />
                                    <span className="text-[9px] uppercase tracking-wide opacity-60">{label}</span>
                                </div>
                            ));
                        })()}
                    </div>

                    {/* Ce qui a fait le score.
                     *
                     * Le « meilleur tour » vivait ici tant que la tuile parlait
                     * de dix tours ; devant les chiffres d'UNE sortie, il
                     * comparerait une matinée à un record sans rien en dire.
                     * Ces quatre-là, eux, expliquent l'écart au par qu'on vient
                     * de lire juste au-dessus : deux fractions pour ce qui a
                     * été tenu, deux comptes pour ce qui a coûté.
                     *
                     * Chacun se tait quand il n'a rien à dire, et pour deux
                     * raisons différentes. Les greens, les fairways et le sable
                     * se déduisent des zones d'arrivée : un tour joué sans les
                     * saisir n'en porte aucun, et « 0/18 » serait un reproche
                     * adressé à quelqu'un qui a bien joué. Les pénalités, elles,
                     * se saisissent une par une : zéro veut dire zéro, et c'est
                     * une bonne nouvelle qu'on n'écrit pas en ligne de compte. */}
                    {mesures.length > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-3 border-t border-base-300 pt-3 text-center">
                            {mesures.map((m, i) => (
                                /* Un dernier tout seul prend la largeur : laissé
                                   dans sa colonne, il pendrait à gauche. */
                                <div key={m.mot} className={
                                    i === mesures.length - 1 && mesures.length % 2 === 1
                                        ? 'col-span-2' : undefined}>
                                    <div className="font-score text-lg">{m.valeur}</div>
                                    <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-60">
                                        {m.mot}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
                );
            })()}

            {/* À l'entraînement, symétrique du bloc parcours : un résumé
                d'un coup d'œil, le détail — la courbe, les fautes, le sac
                club par club — vit dans Statistiques plutôt que d'allonger
                l'accueil d'un bilan complet à chaque ouverture. */}
            {!loading && hasData && stats && (
                <section className="rounded-box bg-base-100 shadow-md p-4">
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                            <Target size={16} className="text-primary" /> À l'entraînement
                        </h2>
                        <Link to="/statistiques" state={{ tab: 'practice' }}
                            className="flex items-center gap-0.5 text-xs font-semibold text-primary">
                            Détail <ChevronRight size={14} />
                        </Link>
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        <div>
                            <div className="font-score text-2xl">
                                {recentRate != null ? `${recentRate}%` : '—'}
                            </div>
                            <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-60">
                                réussite, {stats.recent.sessions > 1 ? `${stats.recent.sessions} séances` : 'dernière séance'}
                            </div>
                        </div>
                        <div>
                            <div className="font-score text-2xl">{stats.overview.sessions30d}</div>
                            <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-60">séances 30j</div>
                        </div>
                        <div>
                            <div className="font-score text-2xl">{stats.overview.streakWeeks}</div>
                            <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-60">sem. d'affilée</div>
                        </div>
                    </div>

                    {/* La pente en barres, même vocabulaire que le score : une
                        hauteur par jour d'entraînement plutôt qu'un trait qui
                        suppose de savoir lire une courbe. */}
                    {stats.trend.length >= 2 && (
                        <div className="mt-4 flex items-stretch gap-1" style={{ height: '2.6rem' }}>
                            {(() => {
                                const rates = stats.trend.map((p) => (p.successRate == null ? 0 : Number(p.successRate)));
                                const max = Math.max(1, ...rates);
                                return rates.map((r, i) => (
                                    <div key={i} className="flex flex-1 flex-col items-center justify-end">
                                        <div className="w-full rounded-t bg-primary"
                                            style={{ height: `${Math.max(6, (r / max) * 100)}%` }} />
                                    </div>
                                ));
                            })()}
                        </div>
                    )}
                </section>
            )}

            {/* ── La boucle fermée ──────────────────────────────
                Le diagnostic ne sert à rien s'il reste une lecture. Ce
                bloc le transforme en un geste : un club, une raison, un
                bouton. C'est la seule chose de cette page qui change ce
                que le joueur fera la prochaine fois. */}
            {/* Carte pleine, pas un voile : `bg-primary/10` laissait passer
                le fairway sombre, et le texte encre y devenait illisible. */}
            {suggestion && (
                <section className="rounded-box border-2 border-primary bg-base-100 p-4 shadow-md">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                        À travailler la prochaine fois
                    </p>
                    <h2 className="mt-1 text-lg font-bold">{suggestion.titre}</h2>
                    <p className="mt-1 text-sm opacity-80">{suggestion.raison}</p>
                    <Link to="/entrainement/exercices"
                        className="btn btn-primary btn-block mt-3 h-14 gap-2 text-lg font-bold tap-target">
                        <Target size={20} /> Lancer un exercice
                    </Link>
                </section>
            )}

            {neglected && (
                <Link to="/entrainement"
                    className="flex items-center gap-2 rounded-box bg-base-100 shadow-md px-4 py-3 text-sm transition-colors hover:bg-base-300">
                    <span className="min-w-0 flex-1">
                        <span className="opacity-60">
                            {neglected.sessionCount === 0
                                ? 'Jamais essayé : ' : 'Pas travaillé depuis un moment : '}
                        </span>
                        <span className="font-semibold">{modeLabel(neglected.mode)}</span>
                        {neglected.lastSessionAt && (
                            <span className="opacity-60">
                                {' '}— dernière fois le{' '}
                                {new Date(neglected.lastSessionAt).toLocaleDateString('fr-FR', {
                                    day: 'numeric', month: 'long',
                                })}
                            </span>
                        )}
                    </span>
                    <ChevronRight size={16} className="shrink-0 opacity-40" />
                </Link>
            )}

            {/* Pas de carte « Mon matériel » ici : le tiroir porte déjà
                l'entrée, et l'accueil doit rester le tableau de bord — pas
                un second menu. */}

            {loading && (
                <div className="flex justify-center py-10">
                    <span className="loading loading-ring loading-lg text-primary" />
                </div>
            )}

            {!loading && !hasData && (
                /* Seul emplacement de la page qui reste SUR le fairway, sans
                 * carte claire : c'est un état vide, pas une donnée, et le
                 * texte reste donc en blanc — hérité de `.fairway-texture` —
                 * plutôt que de forcer le rappel `text-base-content` du reste
                 * de la page, qui y serait illisible. */
                <div className="rounded-box border border-dashed border-white/25 p-6 text-center">
                    <p className="text-sm text-white/80">
                        Vos statistiques apparaîtront ici après votre première séance terminée.
                    </p>
                    <p className="mt-2 text-xs text-white/55">
                        Une séance abandonnée n'est pas conservée : elle ne compte donc pas.
                    </p>
                </div>
            )}
        </div>
    );
}
